import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Button, Box, Typography, Divider, IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';

const ProjectSettings = ({ open, project, onClose, onSave, onDuplicate, onDuplicateTemplate, onDelete }) => {
  const [name, setName] = useState('');
  const [access, setAccess] = useState('team_comment');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (project) {
      setName(project.name || '');
      setAccess(project.access || 'team_comment');
    }
  }, [project]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave?.({ name, access });
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = () => {
    if (window.confirm(`Delete "${project?.name}"? This cannot be undone.`)) {
      onDelete?.();
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 15 }}>Project settings</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          label="Project name" fullWidth size="small" value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
        />

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Access</InputLabel>
          <Select value={access} label="Access" onChange={(e) => setAccess(e.target.value)} sx={{ borderRadius: 1.5 }}>
            <MenuItem value="private">Private — only you</MenuItem>
            <MenuItem value="team_comment">Teammates can comment</MenuItem>
            <MenuItem value="team_edit">Teammates can edit</MenuItem>
          </Select>
        </FormControl>

        <Button
          fullWidth variant="outlined" size="small" startIcon={<ContentCopyIcon />}
          onClick={handleCopyLink}
          sx={{ mb: 2, borderRadius: 1.5, textTransform: 'none', fontSize: 13 }}
        >
          {copied ? 'Copied!' : 'Copy link'}
        </Button>

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            fullWidth variant="outlined" size="small"
            onClick={() => { onDuplicate?.(); onClose?.(); }}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: 13 }}
          >
            Duplicate project
          </Button>
          <Button
            fullWidth variant="outlined" size="small"
            onClick={() => { onDuplicateTemplate?.(); onClose?.(); }}
            sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: 13 }}
          >
            Duplicate as template
          </Button>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Button
          fullWidth variant="outlined" size="small" color="error"
          onClick={handleDelete}
          sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: 13 }}
        >
          Delete project
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small" sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          onClick={handleSave} variant="contained" size="small" disabled={!name.trim() || saving}
          sx={{ background: '#1a1a1a', borderRadius: 1.5, textTransform: 'none', '&:hover': { background: '#333' } }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectSettings;
